// create a context for the registration setup

import { Registration } from '@sportninja/common/types'
import {
  RegistrationTypes,
  Tax,
  AnalyticsData,
} from '@sportninja/common/types/Registration'
import req from '@sportninja/common/api/request'
import dayjs from 'dayjs'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useRegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { useWaivers, Waiver } from '@sportninja/common/hooks/useWaivers'
import { useAccount, AccountData } from '@sportninja/common/hooks/useAccount'
import {
  CustomForm,
  useCustomForm,
} from '@sportninja/common/hooks/useCustomForm'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { sanitizeContentForSave } from 'src/utils/sanitizeContentForSave'
import { getErrorMessage } from '@sportninja/common/utils/utils'

type RegistrationSetupState = {
  loading: boolean
  competitionDateRange: {
    starts_at: string | null
    ends_at: string | null
  }
  goLive: boolean
  registrationType: 'player' | 'team'
  paidBy: 'player' | 'team'
  bccEmails: string[]
  currency_country_id: string
  taxes: string[]
  customBackgroundImage: string
  customBackgroundImageChanged: boolean
  darkMode: boolean
  waivers: string[]
  registrationForm: {
    [key: string]: boolean
  }
  fieldErrors: string[]
  customError: any
  payment_type: string
  autoApproveRegistrations: boolean
  enableTeamRegistration: boolean
  enablePlayerRegistration: boolean
  payment_provider: string | null
  show_waiver: boolean
  addPlayersOutsideOfRegistration: boolean
  player_form: CustomForm | null
  team_form: CustomForm | null
  headerContent: string
  teamConfirmationEmailContent?: string | null
  playerConfirmationEmailContent?: string | null
}

type RegistrationSetupContextType = {
  state: RegistrationSetupState
  setState: React.Dispatch<React.SetStateAction<RegistrationSetupState>>
  registrationTypes: RegistrationTypes
  availableTaxes: Tax[]
  submitRegistration: () => Promise<void>
  isUpdated: boolean
  apiWaivers: Waiver[]
  refetchWaivers: () => void
  apiAccount: AccountData | null
  registrationId: string | null
  registration: Registration | null
  analytics: AnalyticsData | null
  setCustomErroByField: (field: string, message: string) => void
  clearCustomErrors: () => void
  createPlayerForm: () => Promise<CustomForm | null>
  createTeamForm: () => Promise<CustomForm | null>
  scheduleId: string
  activeTab: string
  setActiveTab: React.Dispatch<React.SetStateAction<string>>
}

const RegistrationSetupContext = createContext<
  RegistrationSetupContextType | undefined
>(undefined)

export const useRegistrationSetupContext = () => {
  const context = useContext(RegistrationSetupContext)
  if (!context) {
    throw new Error(
      'useRegistrationSetupContext must be used within a RegistrationSetupContextWrapper'
    )
  }
  return context
}

type RegistrationSetupProps = {
  registration: Registration | null
  registrationTypes: RegistrationTypes
  availableTaxes: Tax[]
  scheduleId: string
  accountId: string
  isRegistrationFormOpen: boolean
}

type RegistrationSetupContextWrapperProps = RegistrationSetupProps & {
  children: React.ReactNode
}

const RegistrationSetupContextWrapper = ({
  children,
  registration,
  registrationTypes,
  availableTaxes,
  scheduleId,
  accountId,
  isRegistrationFormOpen = false,
}: RegistrationSetupContextWrapperProps) => {
  const [state, setState] = useState<RegistrationSetupState>({
    show_waiver: false,
    competitionDateRange: {
      starts_at: '',
      ends_at: '',
    },
    goLive: false,
    registrationType: 'team',
    paidBy: 'player',
    bccEmails: [],
    currency_country_id: '',
    taxes: [],
    customBackgroundImage: '',
    customBackgroundImageChanged: false,
    darkMode: false,
    waivers: [],
    registrationForm: {},
    fieldErrors: [],
    customError: '',
    loading: false,
    payment_type: '1',
    autoApproveRegistrations: isCanlan ? false : true,
    enableTeamRegistration: true,
    enablePlayerRegistration: true,
    addPlayersOutsideOfRegistration: false,
    payment_provider: null,
    player_form: null,
    team_form: null,
    headerContent: '',
    teamConfirmationEmailContent: '',
    playerConfirmationEmailContent: '',
  })

  const [activeTab, setActiveTab] = useState('0')
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)

  const { data: apiWaivers, refetch: refetchWaivers } = useWaivers(accountId)

  const { data: apiAccount, refetch: refetchAccount } = useAccount(accountId)

  const { createForm } = useCustomForm()
  const alreadyCreatedPlayerForm = useRef(false)
  const alreadyCreatedTeamForm = useRef(false)

  useEffect(() => {
    if (isRegistrationFormOpen) {
      refetchAccount()
      req(`/registration/${scheduleId}/admin/analytics`)
        .then((res) => setAnalytics(res.data || null))
        .catch(() => setAnalytics(null))
    }
  }, [isRegistrationFormOpen, refetchAccount, scheduleId])

  const isUpdated = useMemo(
    () => Boolean(registration && registration.id),
    [registration]
  )

  const {
    createRegistration,
    updateRegistration,
    uploadRegistrationImage,
    deleteRegistrationImage,
  } = useRegistrationData('')

  // Helper function to update registration with form IDs only
  const updateRegistrationWithFormId = useCallback(
    async (formType: 'player' | 'team', formId: string) => {
      if (!isUpdated) {
        return // Only update if registration already exists
      }

      try {
        const updatePayload = {
          scheduleId: scheduleId,
          allowPlayerRegistration: state.enablePlayerRegistration,
          registrationType:
            state.enableTeamRegistration && !state.enablePlayerRegistration
              ? 'team'
              : 'player',
          enableRegistration: true,
          eventNumber: '',
          currency_country_id: state.currency_country_id,
          costPerRegistration: 0,
          paymentMethod: Number(state.payment_type),
          goLive: state.goLive,
          dateRange: {
            starts_at: state.competitionDateRange.starts_at || '',
            ends_at: state.competitionDateRange.ends_at || '',
          },
          useDarkTheme: state.darkMode,
          maxTeamSlots: 0,
          defaultTaxes: state.taxes,
          bccEmails: state.bccEmails,
          paymentProvider: state.payment_provider
            ? Number(state.payment_provider)
            : null,
          autoApproveRegistrations: state.autoApproveRegistrations,
          enableTeamRegistration: state.enableTeamRegistration,
          enablePlayerRegistration: state.enablePlayerRegistration,
          addPlayersOutsideOfRegistration:
            state.addPlayersOutsideOfRegistration,
          show_waiver: state?.show_waiver === true ? true : false,
          waivers: state?.waivers || [],
          player_form_id:
            formType === 'player' ? formId : state?.player_form?.id || null,
          team_form_id:
            formType === 'team' ? formId : state?.team_form?.id || null,
          header_content: state.headerContent
            ? sanitizeContentForSave(state.headerContent)
            : null,
          team_confirmation_email_content: state.enableTeamRegistration
            ? sanitizeContentForSave(state.teamConfirmationEmailContent || '')
            : null,
          player_confirmation_email_content: state.enablePlayerRegistration
            ? sanitizeContentForSave(state.playerConfirmationEmailContent || '')
            : null,
        }

        await updateRegistration(updatePayload)
      } catch (error) {
        console.error(
          `❌ updateRegistrationWithFormId: Failed to update registration with ${formType} form ID:`,
          {
            error,
            formType,
            formId,
            scheduleId,
            isUpdated,
          }
        )
        // Don't throw error here to avoid breaking the form creation flow
      }
    },
    [isUpdated, updateRegistration, scheduleId, state]
  )

  const createPlayerForm = useCallback(async () => {
    if (state.player_form) {
      return state.player_form
    }

    if (alreadyCreatedPlayerForm.current) {
      return state.player_form
    }

    try {
      const newForm = await createForm({
        name: `${scheduleId}_player_form`,
        id: `${scheduleId}_player_form`,
      })

      alreadyCreatedPlayerForm.current = true

      // Update state with the new form
      setState((prev) => {
        return { ...prev, player_form: newForm }
      })

      // Immediately update the registration with the new form ID
      if (newForm?.id) {
        await updateRegistrationWithFormId('player', newForm.id)
      }
      return newForm
    } catch (error) {
      console.error('❌ createPlayerForm: Error during player form creation', {
        error,
        scheduleId,
        isUpdated,
      })
      throw error
    }
  }, [
    state.player_form,
    createForm,
    scheduleId,
    setState,
    updateRegistrationWithFormId,
    isUpdated,
  ])

  const createTeamForm = useCallback(async () => {
    if (alreadyCreatedTeamForm.current) {
      return state.team_form
    }

    if (state.team_form) {
      return state.team_form
    }

    try {
      const newForm = await createForm({
        name: `${scheduleId}_team_form`,
        id: `${scheduleId}_team_form`,
      })

      alreadyCreatedTeamForm.current = true

      // Update state with the new form
      setState((prev) => {
        return { ...prev, team_form: newForm }
      })

      // Immediately update the registration with the new form ID
      if (newForm?.id) {
        await updateRegistrationWithFormId('team', newForm.id)
      }

      return newForm
    } catch (error) {
      console.error('❌ createTeamForm: Error during team form creation', {
        error,
        scheduleId,
        isUpdated,
      })
      throw error
    }
  }, [
    state.team_form,
    createForm,
    scheduleId,
    setState,
    updateRegistrationWithFormId,
    isUpdated,
  ])

  const setCustomErroByField = (field: string, message: string) => {
    setState((prev) => ({ ...prev, fieldErrors: [...prev.fieldErrors, field] }))
    setState((prev) => ({
      ...prev,
      customError: { ...prev.customError, [field]: message },
    }))
  }

  const clearCustomErrors = () => {
    setState((prev) => ({ ...prev, fieldErrors: [], customError: {} }))
  }

  const setCustomErrors = (invalid_fields: any) => {
    if (invalid_fields.starts_at || invalid_fields.ends_at) {
      setCustomErroByField(
        'competitionDateRange',
        invalid_fields.starts_at || invalid_fields.ends_at
      )
    }
    if (invalid_fields.waivers) {
      setCustomErroByField('waivers', invalid_fields.waivers)
    }
    if (invalid_fields.name || invalid_fields.name_full) {
      setCustomErroByField('name', invalid_fields.name)
    }
    if (invalid_fields.currency_country_id) {
      setCustomErroByField(
        'currency_country_id',
        invalid_fields.currency_country_id
      )
    }
    if (invalid_fields.payment_type) {
      setCustomErroByField('payment_type', invalid_fields.payment_type)
    }
  }

  /**
   * Determines if today (in UTC) falls inclusively between the
   * competition's start and end dates (also in UTC).
   * https://sportninja.atlassian.net/browse/SN-6414
   *
   * @remarks
   * - All date comparisons are performed in UTC and at the start of the day.
   * - Returns false if either start or end date is missing.
   *
   * @returns {boolean} True if today is between or equal to starts_at and ends_at, false otherwise.
   *
   * @example
   * // If today is 2024-06-10, starts_at is 2024-06-09, ends_at is 2024-06-11
   * // returns true
   */
  const isTodayBetweenStartsAtAndEndsAt = useMemo((): boolean => {
    const { starts_at, ends_at } = state.competitionDateRange
    // Ensure UTC comparison for all dates
    if (!starts_at || !ends_at) {
      return false
    }
    const todayUtc = dayjs().utc().startOf('day')
    const startsAtUtc = dayjs(starts_at).utc().startOf('day')
    const endsAtUtc = dayjs(ends_at).utc().startOf('day')
    // Inclusive: today >= starts_at && today <= ends_at (all in UTC)
    return (
      (todayUtc.isSame(startsAtUtc) || todayUtc.isAfter(startsAtUtc)) &&
      (todayUtc.isSame(endsAtUtc) || todayUtc.isBefore(endsAtUtc))
    )
  }, [state.competitionDateRange])

  const submitRegistration = async () => {
    try {
      setState((prev) => ({
        ...prev,
        fieldErrors: [],
        customError: '',
        loading: true,
      }))

      if (state.show_waiver && state.waivers.length === 0) {
        const error = new Error(
          'Please select at least one waiver'
        ) as Error & {
          invalid_fields?: Record<string, string>
        }
        error.invalid_fields = { waivers: 'Please select at least one waiver' }
        setActiveTab('2')
        throw error
      }

      // Handle registration data
      if (isUpdated) {
        await updateRegistration({
          scheduleId: scheduleId,
          allowPlayerRegistration: state.enablePlayerRegistration,
          registrationType:
            state.enableTeamRegistration && !state.enablePlayerRegistration
              ? 'team'
              : 'player',
          enableRegistration: true,
          eventNumber: '',
          currency_country_id: state.currency_country_id,
          costPerRegistration: 0,
          paymentMethod: Number(state.payment_type),
          // https://sportninja.atlassian.net/browse/SN-6414
          goLive: isTodayBetweenStartsAtAndEndsAt ? true : state.goLive,
          dateRange: {
            starts_at: state.competitionDateRange.starts_at || '',
            ends_at: state.competitionDateRange.ends_at || '',
          },
          useDarkTheme: state.darkMode,
          maxTeamSlots: 0,
          defaultTaxes: state.taxes,
          bccEmails: state.bccEmails,
          paymentProvider: state.payment_provider
            ? Number(state.payment_provider)
            : null,
          autoApproveRegistrations: state.autoApproveRegistrations,
          enableTeamRegistration: state.enableTeamRegistration,
          enablePlayerRegistration: state.enablePlayerRegistration,
          addPlayersOutsideOfRegistration:
            state.addPlayersOutsideOfRegistration,
          show_waiver: state?.show_waiver === true ? true : false,
          waivers: state?.waivers || [],
          player_form_id: state?.player_form?.id || null,
          team_form_id: state?.team_form?.id || null,
          header_content: state.headerContent
            ? sanitizeContentForSave(state.headerContent)
            : null,
          team_confirmation_email_content: state.enableTeamRegistration
            ? sanitizeContentForSave(state.teamConfirmationEmailContent || '')
            : null,
          player_confirmation_email_content: state.enablePlayerRegistration
            ? sanitizeContentForSave(state.playerConfirmationEmailContent || '')
            : null,
        })
      } else {
        await createRegistration({
          scheduleId: scheduleId,
          allowPlayerRegistration: state.registrationType === 'player',
          registrationType: state.registrationType,
          enableRegistration: true,
          eventNumber: '',
          currency_country_id: state.currency_country_id,
          costPerRegistration: 0,
          paymentMethod: Number(state.payment_type),
          // https://sportninja.atlassian.net/browse/SN-6414
          goLive: isTodayBetweenStartsAtAndEndsAt ? true : state.goLive,
          dateRange: {
            starts_at: state.competitionDateRange.starts_at || '',
            ends_at: state.competitionDateRange.ends_at || '',
          },
          useDarkTheme: state.darkMode,
          maxTeamSlots: 0,
          defaultTaxes: state.taxes,
          bccEmails: state.bccEmails,
          paymentProvider: state.payment_provider
            ? Number(state.payment_provider)
            : null,
          autoApproveRegistrations: state.autoApproveRegistrations,
          enableTeamRegistration: state.enableTeamRegistration,
          enablePlayerRegistration: state.enablePlayerRegistration,
          addPlayersOutsideOfRegistration:
            state.addPlayersOutsideOfRegistration,
          show_waiver: state?.show_waiver === true ? true : false,
          waivers: state?.waivers || [],
          player_form_id: state?.player_form?.id || null,
          team_form_id: state?.team_form?.id || null,
          header_content: state.headerContent
            ? sanitizeContentForSave(state.headerContent)
            : null,
          team_confirmation_email_content: state.enableTeamRegistration
            ? sanitizeContentForSave(state.teamConfirmationEmailContent || '')
            : null,
          player_confirmation_email_content: state.enablePlayerRegistration
            ? sanitizeContentForSave(state.playerConfirmationEmailContent || '')
            : null,
        })
      }

      // Handle image upload/deletion
      if (state.customBackgroundImageChanged) {
        if (state.customBackgroundImage !== '') {
          await uploadRegistrationImage(scheduleId, state.customBackgroundImage)
        } else {
          await deleteRegistrationImage(scheduleId)
        }
      }
    } catch (error: any) {
      if (error?.invalid_fields) {
        setCustomErrors(error?.invalid_fields)
        throw error
      }
      const errorMessage = getErrorMessage(error)
      throw new Error(errorMessage)
    } finally {
      setState((prev) => ({ ...prev, loading: false }))
    }
  }

  const value: RegistrationSetupContextType = {
    state,
    setState,
    registrationTypes,
    availableTaxes,
    isUpdated,
    submitRegistration,
    apiWaivers,
    refetchWaivers,
    apiAccount,
    registrationId: registration?.id || null,
    registration,
    analytics,
    setCustomErroByField,
    clearCustomErrors,
    createPlayerForm,
    createTeamForm,
    scheduleId,
    activeTab,
    setActiveTab,
  }

  useEffect(() => {
    if (registration) {
      const paymentProviderId = isUpdated
        ? registration?.payment_provider_type?.id
        : apiAccount?.payment_provider_type?.id || null
      let currency_country_id = String(registration?.currency_country?.id || '')
      let playerForm = registration?.player_form
      let teamForm = registration?.team_form
      setState((prevState) => ({
        ...prevState,
        competitionDateRange: {
          starts_at: registration?.starts_at || '',
          ends_at: registration?.ends_at || '',
        },
        currency_country_id: currency_country_id,
        payment_provider: paymentProviderId,
        taxes: registration?.taxes?.map((tax) => tax.id) || [],
        bccEmails: registration?.bcc_registrations_to?.split(',') || [],
        goLive: registration?.is_live,
        registrationType:
          registration?.registration_type_id === 1 ? 'team' : 'player',
        paidBy: 'team',
        darkMode: registration?.dark_theme,
        customBackgroundImage: registration?.image?.full_path || '',
        customBackgroundImageChanged: false,
        enableTeamRegistration: registration?.is_team_registration,
        enablePlayerRegistration: registration?.is_player_registration,
        autoApproveRegistrations: isCanlan ? false : registration?.auto_approve,
        show_waiver: registration?.show_waiver,
        waivers: registration?.waivers?.map((waiver) => waiver.id) || [],
        addPlayersOutsideOfRegistration:
          registration?.can_add_players_from_roster,
        player_form: playerForm,
        team_form: teamForm,
        headerContent: registration?.header_content || '',
        teamConfirmationEmailContent:
          (registration as any)?.team_confirmation_email_content || '',
        playerConfirmationEmailContent:
          (registration as any)?.player_confirmation_email_content || '',
      }))
    }
  }, [registration, isUpdated, apiAccount])

  return (
    <RegistrationSetupContext.Provider value={value}>
      {children}
    </RegistrationSetupContext.Provider>
  )
}

export { RegistrationSetupContextWrapper }
